import { useState, type Dispatch, type SetStateAction } from 'react';

import type {
  ChatUiFlightOffer,
  ChatUiMoney,
  ChatUiMultiLeg,
  FlightTariffView,
} from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { fmtMoneyWhole } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { sumMoney, type LegStep } from '@voll-intelligence/intelligence-ui-kit';
import { defaultSel, type FareSel, type TariffFetchState } from '../../shared/ui/fare_expansion.js';
import { useFlightTariffPort } from '../../shared/flight_tariff_port.js';
import { multiLegSelectionOf } from '../../shared/multi_leg_wire.js';
import { OfferListBody, useOfferFares, type OfferFares } from '../../shared/offer_list.js';
import { useResearchOffers } from '../../shared/use_research_offers.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { FlightExplorerDialog } from '../../shared/explorer/explorer_dialog.js';
import { type ExplorerConfirm, type ExplorerPick } from '@voll-intelligence/intelligence-ui-kit';
import type { FlightSearchParams } from '@voll-intelligence/intelligence-ui-kit';
import { explorerOpenRequest } from './explorer_open_request.js';
import { ItinerarySkirt } from './itinerary_skirt.js';
import { LegStepper } from './leg_stepper.js';
import { firstPendingLeg } from './board_wire.js';
import { useDispatchPicksToHost } from './use_dispatch_picks_to_host.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';

/** O que UM trecho fechou. O `offerId` não é enfeite: é o que faz o card escolhido
 *  continuar marcado (e a escada reabrir nele) quando a pessoa volta no trecho. */
interface LegPick {
  offerId: string;
  fareId: string;
  price: ChatUiMoney;
}

export type Picks = Record<number, LegPick>;

/**
 * O tabuleiro do roteiro: as escolhas correntes e para onde o `Desfazer` volta. Os
 * dois num ESTADO SÓ porque eles competem — o lote das tarifas mais baratas pousa centenas
 * de ms depois do clique, e nessa janela a pessoa pode fechar um trecho à mão (é
 * exatamente o que faz quem se impacienta com o spinner). Em dois `useState` a
 * escrita do lote se monta a partir do `picks` que ele mediu NO CLIQUE e atropela
 * esse pick — perda no caminho do dinheiro e indetectável depois: sem erro, sem
 * log, só uma tarifa que não é a que a pessoa escolheu. Com um estado, cada escrita
 * deriva os DOIS campos do mesmo `prev`, dentro do mesmo updater.
 *
 * E `setState` nunca é chamado de dentro de um updater: o React invoca updaters
 * duas vezes em StrictMode (a web-chat monta em StrictMode), então efeito ali é
 * efeito em dobro.
 */
interface Board {
  picks: Picks;
  /** Os picks de antes do último lote. `null` = não há lote a desfazer. */
  undo: Picks | null;
}

/** Hidrata as escolhas do eco do servidor — presente na re-emissão pós-cota e no
 *  card congelado do turno de sucesso. Ausente no primeiro card ⇒ `{}`. */
function picksFromEcho(legs: ChatUiMultiLeg[]): Picks {
  const picks: Picks = {};
  for (const [i, leg] of legs.entries()) {
    if (leg.chosen) picks[i] = leg.chosen;
  }
  return picks;
}

/**
 * Assinatura do que o SERVIDOR apresentou: o eco de cada trecho MAIS as ofertas em
 * tela. É o que separa "o servidor falou de novo" de "a pessoa clicou" — e ela tem
 * dois termos de propósito, os dois necessários:
 *
 *  - o **eco**, porque a re-emissão pós-cota volta com o trecho recusado VAZIO;
 *  - as **ofertas**, porque trio novo é DECISÃO NOVA. Uma re-busca re-emite o
 *    mesmo bloco (o produtor usa id FIXO, justamente para reconciliar no lugar)
 *    com outras ofertas e o mesmo eco vazio: manter a escolha local ali deixaria o
 *    CTA despachando um `offerId` que já saiu da tela.
 *
 * Comparada por VALOR, não por identidade: re-render com o mesmo bloco não mexe em
 * nada. Não simplifique de volta para só o eco.
 */
function presentedKey(legs: ChatUiMultiLeg[]): string {
  return legs
    .map((l) =>
      [
        l.chosen ? `${l.chosen.offerId}:${l.chosen.fareId}` : '-',
        ...l.offers.map((o) => o.offerId),
      ].join(','),
    )
    .join('|');
}

/** O fareId canônico da escolha corrente da escada: o combo quando a oferta é
 *  bundled, senão a tarifa da ida. Multi-trecho é one-way por trecho, então não há
 *  par `ida|volta` — o wire leva UM id. */
function fareIdOf(state: TariffFetchState, sel: FareSel): string | null {
  if (state.phase !== 'ready') return null;
  return (state.view.bundled.length > 0 ? sel.bundled : sel.outbound) ?? null;
}

/** O preço da tarifa `fareId` nesta escada — o que o PAINEL imprimiria nela. No
 *  bundled o preço do combo é o TOTAL DO PAR, que o provider carrega em CADA
 *  perna: somar as duas dobraria o valor. */
function priceOfFare(view: FlightTariffView, fareId: string): ChatUiMoney | null {
  const combo = view.bundled.find((c) => c.fareId === fareId);
  if (combo) return combo.departing.price;
  return view.departing.find((f) => f.fareId === fareId)?.price ?? null;
}

/**
 * O que o atalho fecha em UM trecho, a partir da escada que chegou. A tarifa é o
 * DEFAULT da escada (`defaultSel` + `fareIdOf`: a mais barata, a que deu o preço
 * do card), e não uma segunda definição: o atalho fecha o trecho na tarifa que o
 * painel abriria pré-selecionada, senão o parcial mostraria um preço que ninguém
 * viu.
 *
 * `null` = escada sem tarifa utilizável, e aí o trecho continua PENDENTE — gravar
 * um pick sem preço faria o parcial afirmar um trecho que não fechou.
 */
function cheapestPick(offer: ChatUiFlightOffer, view: FlightTariffView): LegPick | null {
  const fareId = fareIdOf({ phase: 'ready', view }, defaultSel(view));
  if (!fareId) return null;
  const price = priceOfFare(view, fareId);
  return price ? { offerId: offer.offerId, fareId, price } : null;
}

/** Um trecho que o lote conseguiu fechar. */
type BatchPick = { index: number; pick: LegPick };

/**
 * Busca em paralelo a escada de cada trecho pendente e devolve só os que dá para
 * fechar. Falha é POR TRECHO e o lote NÃO é transação: o trecho cuja tarifa não
 * chegou simplesmente não entra na lista e continua pendente — o placar e o atalho
 * (agora com o número menor) relatam a diferença entre o que o rótulo prometeu e o
 * que aconteceu, e o parcial nunca conta um trecho que ficou de fora.
 *
 * `fares.capture` (não `port.getTariff` direto) é o que fecha a invariante "uma
 * oferta é capturada no máximo UMA vez por vida do card": ele é o MESMO caminho
 * que o toggle manual da escada usa, então uma oferta já `ready` (escada aberta
 * à mão, ou um trecho revisitado) resolve sem pedir `/flight/tariff` de novo, e
 * uma captura já em voo (toggle concorrente com o lote) devolve a MESMA promise
 * em vez de disparar uma segunda. Pedir a MESMA oferta duas vezes não é inócuo:
 * a captura é a pricing option da cotação no modal-session, e uma segunda
 * substitui a primeira — o `fareId` que um pick anterior guardou passa a apontar
 * para um id que não existe mais (QA dev 09/14, conversa E2).
 */
async function resolveCheapest(
  fares: OfferFares,
  pending: { index: number; leg: ChatUiMultiLeg }[],
): Promise<BatchPick[]> {
  const settled = await Promise.all(
    pending.map(async ({ index, leg }) => {
      // A oferta do atalho é `offers[0]`: o produtor põe a curada na frente.
      const offer = leg.offers[0];
      if (!offer) return null;
      try {
        const view = await fares.capture(offer);
        const pick = cheapestPick(offer, view);
        return pick ? { index, pick } : null;
      } catch {
        return null;
      }
    }),
  );
  return settled.filter((r): r is BatchPick => r !== null);
}

/** Escreve o lote no tabuleiro do instante da escrita (não no do clique). */
function applyBatch(prev: Board, closed: BatchPick[]): Board {
  // Só os trechos que AINDA estão pendentes no instante da escrita: um pick
  // que a pessoa fechou à mão enquanto os fetches voavam é o que vale, e o
  // lote não escreve por cima dele.
  const filled = closed.filter(({ index }) => prev.picks[index] === undefined);
  // Lote que não fechou nada não mexeu em nada — nem arma o `Desfazer`, que
  // seria um botão para voltar do estado atual ao estado atual. Cai aqui
  // tanto quando nenhuma tarifa chegou quanto quando a mão chegou antes.
  if (filled.length === 0) return prev;
  const batch: Picks = Object.fromEntries(filled.map(({ index, pick }) => [index, pick]));
  // O snapshot é o tabuleiro de ANTES desta escrita, não o do clique: é o que
  // o lote de fato cobriu, então desfazer tira só o que ele pôs.
  return { picks: { ...prev.picks, ...batch }, undo: prev.picks };
}

/**
 * Os picks do Board no vocabulário do explorer — o que faz abrir a lista completa
 * CONTINUAR o roteiro em vez de recomeçá-lo.
 *
 * O `summary` sai só com preço: o Board guarda oferta, tarifa e valor, e nunca o
 * NOME da tarifa (a escada devolve o nome no clique, e o pick não o retém). No
 * roteiro isso não custa nada — os campos de exibição do `summary` só alimentam o
 * `FarePickChip`, que é gated em `mode !== 'multi_leg'`.
 *
 * Pick cuja oferta não está mais no trio do trecho fica de FORA: o explorer
 * desenha o chip do passo procurando o `offerId` na lista da perna, e um id órfão
 * daria um passo marcado como resolvido sem voo nenhum embaixo.
 */
function explorerPicksOf(
  legs: ChatUiMultiLeg[],
  picks: Picks,
): Partial<Record<number, ExplorerPick>> {
  const out: Partial<Record<number, ExplorerPick>> = {};
  for (const [i, leg] of legs.entries()) {
    const pick = picks[i];
    if (!pick || !leg.offers.some((o) => o.offerId === pick.offerId)) continue;
    out[i] = {
      offerId: pick.offerId,
      fares: { outboundFareId: pick.fareId },
      summary: { total: pick.price.amount, currency: pick.price.currency },
    };
  }
  return out;
}

/** O canal do explorer que o {@link useLegExplorer} entrega ao card. */
interface LegExplorerChannel {
  /** CTA visível? Gated no seam `flightSearch`: sem busca server-side não há
   *  explorer, e o card degrada sem CTA (fail-soft de port ausente). */
  enabled: boolean;
  /** Perna em que o explorer ABRE; `null` = fechado. */
  openLeg: number | null;
  open: (leg: number) => void;
  close: () => void;
  adopt: (c: ExplorerConfirm) => void;
}

/**
 * Estado + desfecho do explorer — FORA do corpo do card (teto de complexidade; o
 * mesmo recorte do `FlightOffersDrawer` no binário). O índice, e não um boolean:
 * é a perna em que o explorer ABRE, capturada no clique.
 *
 * O `adopt` tem DOIS galhos:
 *
 *  - **`leg`** (revisão de UM trecho): merge no tabuleiro, SEM dispatch — o CTA
 *    final do card ainda compromete. É o único galho que fica local.
 *  - **roteiro inteiro** (`multi`, geometria IGUAL ou divergente; `single` = a
 *    aba virou pra binário): despacha AGORA (#2225, Hipótese A), derivado do
 *    confirm do EXPLORER. Antes o `multi` de geometria IGUAL fazia merge e exigia
 *    um 2º "Confirmar" no card — dois gestos rotulados igual, o 1º sem commit;
 *    agora o "Confirmar" da escada é o gesto único, como o divergente e o binário
 *    já eram. O servidor valida sob o FENCE (só o card vivo) e re-emite o card
 *    congelado; um 2º clique cai em `stale_offers_card` — a defesa do replay é o
 *    fence, como no binário. (O divergente NÃO tinha escolha: o Board indexa as
 *    pernas do CARD e não representa a viagem re-formada — o engolir silencioso
 *    `if (c.kind === 'single') return` era o problema de abertura da spec. O de
 *    geometria igual PODERIA fazer merge, mas dispensa a janela de revisão
 *    redundante: o explorer já mostrou o total somado no placar antes do confirm.)
 *
 * `presentedKey` fica INTOCADO de propósito: uma re-emissão do servidor
 * resincroniza o Board e dropa os picks adotados — trio novo é decisão nova.
 */
function useLegExplorer(args: {
  legs: ChatUiMultiLeg[];
  picks: Picks;
  challengeId: string;
  dispatch: GenUiDispatch;
  setBoard: Dispatch<SetStateAction<Board>>;
  setActive: Dispatch<SetStateAction<number>>;
  /** O adopt conclui uma revisão: o card re-colapsa (reset do `expandedOverride`)
   *  — mesma regra do pick local; sem isso, adotar do explorer com o roteiro
   *  completo deixava a lista aberta. */
  onReviewClosed: () => void;
}): LegExplorerChannel {
  const { t } = useGenUiI18n();
  const { flightSearch } = useGenUiConfig();
  const [openLeg, setOpenLeg] = useState<number | null>(null);

  const mergeBoard = (adopted: Picks) => {
    // UMA escrita para os N trechos: em N escritas o `undo: null` de cada uma
    // competiria com a próxima, e o Board é um estado só de propósito.
    args.setBoard((prev) => ({ picks: { ...prev.picks, ...adopted }, undo: null }));
    const pending = args.legs.findIndex(
      (_, i) => adopted[i] === undefined && args.picks[i] === undefined,
    );
    if (pending >= 0) args.setActive(pending);
  };

  const dispatchConfirm = (c: Exclude<ExplorerConfirm, { kind: 'leg' }>) => {
    if (c.kind === 'multi') {
      args.dispatch({
        type: 'interaction',
        interactionId: args.challengeId,
        text: t('flightMultiOffers.pickedAll', {
          count: c.legs.length,
          total: fmtMoneyWhole(c.total),
        }),
        data: multiLegSelectionOf(c.legs),
      });
      return;
    }
    args.dispatch({
      type: 'interaction',
      interactionId: args.challengeId,
      text: t('flightOffers.pickedFares', {
        position: c.summary.position,
        carrier: c.summary.carrier,
        fares: c.summary.fareNames,
      }),
      data: { action: 'select_offer_fares' as const, offerId: c.offerId, ...c.fares },
    });
  };

  const adopt = (c: ExplorerConfirm) => {
    if (c.kind === 'leg') {
      // Revisão de UM trecho: merge local, o CTA final do card ainda compromete.
      mergeBoard({
        [c.legIndex]: {
          offerId: c.offerId,
          fareId: c.fareId,
          price: { amount: c.summary.total, currency: c.summary.currency },
        },
      });
    } else {
      // Roteiro inteiro (multi igual/divergente, ou single): o "Confirmar" da
      // escada é o gesto único — despacha AGORA (#2225 Hipótese A). Antes o multi
      // de geometria igual caía num merge e exigia um 2º "Confirmar" no card.
      dispatchConfirm(c);
    }
    args.onReviewClosed();
    setOpenLeg(null);
  };

  return {
    enabled: Boolean(flightSearch),
    openLeg,
    open: setOpenLeg,
    close: () => {
      setOpenLeg(null);
    },
    adopt,
  };
}

/**
 * O explorer do card, aberto no ROTEIRO INTEIRO — não numa perna.
 *
 * Era one-way de uma perna só, e o preço disso era alto: quem abria "ver todos os
 * voos deste trecho" num roteiro de 3 saía do explorer com UM trecho fechado e
 * tinha de reabri-lo mais duas vezes. O motor sempre soube fazer multi (fan-out
 * por perna, stepper de N passos, confirm agregado); faltava o card PEDIR.
 *
 * O que atravessa junto, e por quê:
 *  - **o trio de cada trecho** como semente, então os N passos abrem com lista em
 *    cena em vez de N esqueletos de 12–40s enquanto o fan-out corre;
 *  - **a perna do clique** (`initialLeg`), senão o explorer abriria no trecho 0 e
 *    a pessoa perderia o lugar;
 *  - **os picks do Board** (`initialChosen`), senão abrir o explorer custaria
 *    refazer o que o card já tinha fechado. Só o preço viaja no `summary`: o
 *    Board não guarda nome de tarifa, e o roteiro não renderiza esse campo.
 *
 * A faixa de busca EDITA (decisão de produto 2026-08-02): re-formar o roteiro
 * aqui dentro é o gesto suportado — o confirm divergente é DESPACHADO na hora
 * (ver `useLegExplorer.adopt`), o servidor valida sob o fence e re-emite o card
 * do roteiro novo congelado. A virada pra uma aba binária também vale: o ciclo
 * multi aceita o pick binário fenced e re-forma (o espelho do caminho
 * binário→multi que o #1523 abriu).
 *
 * Roteiro de UM trecho (o schema permite, `legs.min(1)`) segue one_way com
 * `legIndex`: um stepper de um passo não descreve viagem nenhuma, e ali o confirm
 * `single` precisa do rótulo `leg`.
 */
function LegExplorerDrawer({
  channel,
  challengeId,
  legs,
  picks,
  search,
  readOnly,
}: Readonly<{
  channel: LegExplorerChannel;
  /** Linhagem: o challengeId do card vai em cada busca de perna (`itinerary`). */
  challengeId: string;
  legs: ChatUiMultiLeg[];
  picks: Picks;
  search?: Pick<FlightSearchParams, 'cabin' | 'policyTravelerId'>;
  readOnly?: boolean;
}>) {
  if (channel.openLeg === null || legs.length === 0) return null;
  const single = legs.length === 1;
  const explorerPicks = explorerPicksOf(legs, picks);
  // Poda PRÉ-mount: pick do Board cuja oferta saiu do trio não entra no explorer
  // — o reducer nunca a vê, então o descarte viaja como flag e o estado nasce com
  // `picksDiscarded` ligado (spec, rodada 15 da emenda #1510).
  const prunedAtMount = Object.keys(picks).length > Object.keys(explorerPicks).length;
  return (
    <FlightExplorerDialog
      {...(single ? { legIndex: channel.openLeg } : {})}
      request={explorerOpenRequest(legs, search, challengeId)}
      seedOffers={legs.map((l) => l.offers)}
      initialLeg={channel.openLeg}
      replacesMultiCard
      initialChosen={explorerPicks}
      initialPicksDiscarded={prunedAtMount}
      readOnly={readOnly}
      onConfirm={channel.adopt}
      onClose={channel.close}
    />
  );
}

/**
 * As edições pontuais do tabuleiro — fora do corpo do card (teto de linhas),
 * fechando só sobre os setters:
 *
 *  - **undoBatch**: volta o roteiro ao instante de antes do lote — o par do
 *    atalho. Updater e não valor: nada de restaurar um snapshot lido num render
 *    que já passou.
 *  - **removePick**: o ✕ do chip — desfaz o pick DE UM trecho e leva a pessoa até
 *    ele (desfez para escolher de novo). O snapshot do lote morre junto (mesma
 *    regra do pick manual) e a revisão fecha (`closeReview`): o trecho reaberto
 *    está incompleto, e o override grudado deixaria a lista aberta no PRÓXIMO
 *    fechamento.
 */
function boardEdits(
  setBoard: Dispatch<SetStateAction<Board>>,
  setActive: Dispatch<SetStateAction<number>>,
  closeReview: () => void,
) {
  return {
    undoBatch: () => {
      setBoard((prev) => (prev.undo ? { picks: prev.undo, undo: null } : prev));
    },
    removePick: (i: number) => {
      setBoard((prev) => {
        if (prev.picks[i] === undefined) return prev;
        const rest = Object.fromEntries(
          Object.entries(prev.picks).filter(([key]) => Number(key) !== i),
        );
        return { picks: rest, undo: null };
      });
      setActive(i);
      closeReview();
    },
  };
}

/** O turno do CTA: os N picks juntos. `null` enquanto faltar trecho — quem
 *  despacha é dono de provar que o roteiro inteiro está no payload. Trechos em
 *  moedas diferentes não somam (o `sumMoney` se cala em vez de inventar um
 *  número), e aí a frase vai sem o total em vez de com um valor inutilizável. */
function confirmTurn(
  legs: ChatUiMultiLeg[],
  picks: Picks,
  t: ReturnType<typeof useGenUiI18n>['t'],
): { text: string; data: ReturnType<typeof multiLegSelectionOf> } | null {
  const chosen = legs.map((_, i) => picks[i]).filter((p): p is LegPick => p !== undefined);
  if (chosen.length !== legs.length) return null;
  const total = sumMoney(chosen.map((p) => p.price));
  return {
    text: t('flightMultiOffers.pickedAll', {
      count: legs.length,
      total: total ? fmtMoneyWhole(total) : '',
    }),
    data: multiLegSelectionOf(chosen.map((p, i) => ({ ...p, legIndex: i }))),
  };
}

/** Os passos do stepper a partir do tabuleiro. O voo do chip sai da OFERTA que
 *  venceu o trecho (multi é one-way por perna — sempre `segments[0]`, sem o caso
 *  volta-do-RT do explorer). Pick cujo offerId saiu do trio (eco órfão) fica sem
 *  chip, mesma régua do `explorerPicksOf`. */
function stepsOf(legs: ChatUiMultiLeg[], picks: Picks): LegStep[] {
  return legs.map((leg, i) => {
    const pick = picks[i];
    const chosenOffer = pick ? leg.offers.find((o) => o.offerId === pick.offerId) : undefined;
    const chosenFlight = chosenOffer?.segments[0];
    return {
      origin: leg.origin,
      destination: leg.destination,
      departDate: leg.departDate,
      ...(pick ? { chosenPrice: pick.price } : {}),
      ...(chosenFlight ? { chosenFlight } : {}),
    };
  });
}

/**
 * O card do roteiro multi-trecho: os N trechos JÁ buscados, um aberto por vez, e
 * UM turno no fim. Escolher trecho é estado deste componente — nada de rede além
 * do port de tarifas que a escada já usava —, e o `interaction` sai só no CTA
 * final, com os N picks juntos.
 *
 * O CTA existe (em vez de o último pick fechar o roteiro sozinho) porque despachar
 * CONGELA o card: auto-disparar tornaria a última escolha irrevogável. Com o CTA,
 * cada trecho é reversível até o commit.
 *
 * Um trecho é um ROUND com a casca de sempre — o trio de cards de voo e a escada
 * de tarifas de UMA direção abrindo dentro do escolhido. Não existe card com N
 * trechos nem escada com N direções.
 */
export function FlightMultiOffersCard({
  legs,
  challengeId,
  blockId,
  note,
  readOnly,
  search,
  dispatch,
}: {
  readonly legs: ChatUiMultiLeg[];
  readonly challengeId: string;
  /** `ui.id` do bloco; sem ele o conjunto não é despachado — o host não teria
   *  como limpar. */
  readonly blockId?: string;
  readonly note?: string;
  /** Card congelado (turno resolvido/conversa encerrada): a escada abre view-only
   *  e o CTA final sai. */
  readonly readOnly?: boolean;
  /** Echo da busca do bloco (grupo F): política/cabine que semeiam o explorer
   *  por trecho — rota+data vêm da própria perna. */
  readonly search?: Pick<FlightSearchParams, 'cabin' | 'policyTravelerId'>;
  readonly dispatch: GenUiDispatch;
}) {
  const { t } = useGenUiI18n();
  const port = useFlightTariffPort();
  const [board, setBoard] = useState<Board>(() => ({ picks: picksFromEcho(legs), undo: null }));
  const { picks } = board;
  const [active, setActive] = useState(() => firstPendingLeg(legs, picks));
  useDispatchPicksToHost({ picks, readOnly, blockId, challengeId, dispatch });
  // O eco não é só estado INICIAL. O bloco reconcilia por id FIXO, então o
  // servidor re-emite ESTE card montado — com outro `chosen` (cota que falhou) ou
  // com outro trio (re-busca). Nos dois casos a verdade passa a ser a dele, e sem
  // resincronizar o card seguiria marcado (e o CTA armado) com um pick que o
  // servidor rejeitou ou que aponta para uma oferta que saiu da tela. Entre
  // re-emissões a assinatura não muda, então escolha local nunca é atropelada.
  const [synced, setSynced] = useState(() => presentedKey(legs));
  const [batching, setBatching] = useState(false);
  /** Revisão aberta com o roteiro COMPLETO (UX Pacote 2, M3): completo colapsa a
   *  lista por padrão; clicar um passo reabre. O ✕ e o resync resetam — o próximo
   *  fechamento colapsa de novo em vez de herdar a revisão antiga. */
  const [expandedOverride, setExpandedOverride] = useState(false);
  // Compartilhado com `boardEdits`: os dois fecham a revisão do mesmo jeito.
  const closeReview = setExpandedOverride.bind(null, false);
  const explorer = useLegExplorer({
    legs,
    picks,
    challengeId,
    dispatch,
    setBoard,
    setActive,
    onReviewClosed: closeReview,
  });
  // Recuperação do stale da escada (#2332): o roteiro refeito é um turno da
  // conversa (#5277) — o explorer é só a porta do "Ver todos os voos".
  const research = useResearchOffers({ challengeId, dispatch, enabled: !readOnly });
  const presented = presentedKey(legs);
  if (presented !== synced) {
    setSynced(presented);
    // O `undo` cai JUNTO com os picks — e é o mesmo objeto de estado, então não há
    // caminho em que um sobreviva ao outro: restaurar picks de um trio que saiu da
    // tela é exatamente o que este resync existe para impedir. O valor é direto (e
    // não um updater) porque o eco do servidor não depende do que havia antes.
    setBoard({ picks: picksFromEcho(legs), undo: null });
    setExpandedOverride(false);
  }

  const offers = legs[active]?.offers ?? [];
  const activePick = picks[active];
  const fares = useOfferFares(offers, {
    enabled: Boolean(port),
    // A escada reabre no card que VENCEU o trecho ao voltar nele — é ele que prova
    // qual dos três ganhou. Trecho ainda pendente abre sem escada.
    autoExpandId: activePick?.offerId ?? null,
    // ...e reabre na tarifa que o trecho FECHOU, não no default. O pick é a
    // semente inteira (o `fareId` dele é o que o eco do servidor trouxe).
    seedFare: activePick ?? null,
  });

  const steps = stepsOf(legs, picks);
  /** Os trechos que ainda faltam — o alvo do atalho e o número do rótulo dele. */
  const pendingLegs = legs.flatMap((leg, i) => (picks[i] === undefined ? [{ index: i, leg }] : []));
  const complete = pendingLegs.length === 0;

  /** Grava o trecho aberto e abre o primeiro PENDENTE — não o vizinho: quem voltou
   *  no trecho 1 para trocar não pode ser jogado num trecho já decidido. Sem
   *  pendente, fica onde está; quem fecha o roteiro é o CTA. */
  const choose = (
    offer: ChatUiFlightOffer,
    _position: number,
    state: TariffFetchState,
    picked: { total: number; currency: string },
    sel: FareSel,
  ) => {
    const fareId = fareIdOf(state, sel);
    if (!fareId) return;
    const pick: LegPick = {
      offerId: offer.offerId,
      fareId,
      // O preço é o que o PAINEL mostrou (a tarifa escolhida), não o preço-vitrine
      // da oferta: o painel é o que a pessoa leu ao decidir.
      price: { amount: picked.total, currency: picked.currency },
    };
    // O snapshot do lote morre no primeiro pick manual: desfazer o lote DEPOIS de
    // alguém trabalhar à mão apagaria trabalho que não era do lote.
    setBoard((prev) => ({ picks: { ...prev.picks, [active]: pick }, undo: null }));
    // Pick fechado = revisão concluída: sem o reset, trocar a tarifa numa revisão
    // deixava a lista aberta com o roteiro completo (o override ficava grudado).
    setExpandedOverride(false);
    // O trecho gravado é o `active`, que o `findIndex` já pula: para os outros
    // índices o tabuleiro deste render é o mesmo de depois da escrita.
    const pending = legs.findIndex((_, i) => i !== active && picks[i] === undefined);
    if (pending >= 0) setActive(pending);
  };

  /**
   * Fecha cada trecho PENDENTE na tarifa mais barata da oferta à frente dele — e
   * só os pendentes: quem já escolheu à mão não é atropelado, porque isto é um
   * atalho para o que falta e não um "refaz tudo do jeito VOLL".
   *
   * NÃO despacha. Despachar CONGELA o card, e seria no mesmo instante em que o
   * `Desfazer` apareceu — quem compromete continua sendo o CTA, e é essa janela que
   * dá tempo à saída.
   */
  const acceptCheapest = () => {
    if (!port || batching) return;
    setBatching(true);
    // `fares.capture` já grava `fetchStates`/`sels` da oferta assim que a
    // promise resolve (dentro de `useOfferFares`) — não há mais nada pra
    // semear aqui: quando este `.then` roda, a escada de cada trecho fechado
    // já está `ready` no MESMO cache que o efeito de `autoExpandId` lê.
    void resolveCheapest(fares, pendingLegs).then((closed) => {
      setBatching(false);
      setBoard((prev) => applyBatch(prev, closed));
    });
  };

  const { undoBatch, removePick } = boardEdits(setBoard, setActive, closeReview);

  const confirmPick = () => {
    const turn = confirmTurn(legs, picks, t);
    if (!turn) return;
    dispatch({ type: 'interaction', interactionId: challengeId, ...turn });
  };

  return (
    <>
      {note && <p className="itin-intro">{note}</p>}
      <LegStepper
        steps={steps}
        active={active}
        onGoTo={(i) => {
          setActive(i);
          // Com o roteiro completo o clique no passo é REVISÃO: reabre a lista.
          if (complete) setExpandedOverride(true);
        }}
        {...(readOnly ? {} : { onRemove: removePick })}
        openOptionsCount={offers.length}
        frozen={readOnly}
        // Roteiro completo colapsa VIVO ou CONGELADO. Vivo, para destacar o
        // Confirmar (M3); congelado, porque o card fica atrás do checkout e a
        // lista "Trecho N · 3 melhores opções" reaberta lia como escolha ainda
        // pendente (QA dev 17/09). O recibo é a fila de passos com os chips; a
        // escada view-only continua a um clique no passo.
        collapsed={complete && !expandedOverride}
      >
        {/* A escada de um trecho do roteiro é UMA perna, mas não é "a ida": só o
            card binário tem ida. O rótulo sai do número do trecho ativo. No
            celular as ofertas do trecho viram carrossel, como no card binário
            (#4879); o "Trecho N" acima delas diz de qual trecho são. */}
        <OfferListBody
          offers={offers}
          fares={fares}
          readOnly={readOnly}
          carousel
          soloLegLabel={t('flightMultiOffers.leg', { n: active + 1 })}
          onSelectFares={choose}
          {...research}
        />
      </LegStepper>
      {!readOnly && (
        <ItinerarySkirt
          {...(explorer.enabled ? { explore: { active, onOpen: explorer.open } } : {})}
          // Sem port não há escada nem tarifa a buscar, e um atalho que não pode
          // fechar nada é pior que atalho nenhum.
          {...(port
            ? {
                shortcut: {
                  pending: pendingLegs.length,
                  undoable: board.undo !== null,
                  busy: batching,
                  onAccept: acceptCheapest,
                  onUndo: undoBatch,
                },
              }
            : {})}
          complete={complete}
          onConfirm={confirmPick}
        />
      )}
      <LegExplorerDrawer
        channel={explorer}
        challengeId={challengeId}
        legs={legs}
        picks={picks}
        search={search}
        readOnly={readOnly}
      />
    </>
  );
}
